import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Tooltip,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import { Add as AddIcon, Save as SaveIcon, Publish as PublishIcon } from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import SectionEditor from './SectionEditor';
import formBuilderService from '../../services/formBuilderService';

const FormBuilder = ({ eventId, initialForm = null, onSave, onPublish, onChange }) => {
  const [form, setForm] = useState({
    id: initialForm?.id,
    title: initialForm?.title || 'Untitled Form',
    description: initialForm?.description || '',
    sections: initialForm?.sections || [],
    is_published: initialForm?.is_published || false,
  });

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState({ open: false, message: '', severity: 'success' });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Notify parent component about form changes
  useEffect(() => {
    if (onChange) {
      onChange(form);
    }
  }, [form, onChange]);

  // Add a new section to the form
  const addSection = useCallback(async () => {
    try {
      setIsLoading(true);
      const newSection = {
        id: `temp-section-${Date.now()}`,
        title: `Section ${form.sections.length + 1}`,
        description: '',
        questions: [],
        isNew: true,
      };

      // If form is saved, create the section in the backend
      if (form.id) {
        const savedSection = await formBuilderService.section.create(form.id, {
          title: newSection.title,
          description: newSection.description,
          order: form.sections.length,
        });

        setForm(prev => ({
          ...prev,
          sections: [
            ...prev.sections,
            { ...savedSection, questions: [] }
          ]
        }));
      } else {
        // If form is not saved yet, just update local state
        setForm(prev => ({
          ...prev,
          sections: [...prev.sections, newSection]
        }));
      }
    } catch (error) {
      console.error('Error adding section:', error);
      setSaveStatus({
        open: true,
        message: 'Failed to add section. Please try again.',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }, [form.id, form.sections.length]);

  // Update a section
  const updateSection = useCallback(async (sectionId, updatedSection) => {
    try {
      // Update local state immediately for better UX
      setForm(prev => ({
        ...prev,
        sections: prev.sections.map(section =>
          section.id === sectionId ? updatedSection : section
        )
      }));

      // If form is saved, update the section in the backend
      if (form.id && !updatedSection.isNew) {
        await formBuilderService.section.update(sectionId, {
          title: updatedSection.title,
          description: updatedSection.description,
          order: updatedSection.order,
        });
      }
    } catch (error) {
      console.error('Error updating section:', error);
      setSaveStatus({
        open: true,
        message: 'Failed to update section. Please try again.',
        severity: 'error',
      });

      // Revert local state on error
      setForm(prev => ({
        ...prev,
        sections: prev.sections.map(section =>
          section.id === sectionId ? { ...section, error: 'Failed to save' } : section
        )
      }));
    }
  }, [form.id]);

  // Delete a section
  const deleteSection = useCallback(async (sectionId) => {
    try {
      // Optimistically remove from UI
      setForm(prev => ({
        ...prev,
        sections: prev.sections.filter(section => section.id !== sectionId)
      }));

      // If form is saved, delete from backend
      if (form.id) {
        await formBuilderService.section.delete(sectionId);
      }
    } catch (error) {
      console.error('Error deleting section:', error);
      setSaveStatus({
        open: true,
        message: 'Failed to delete section. Please try again.',
        severity: 'error',
      });

      // Revert local state on error
      // TODO: Consider a better way to restore the section
    }
  }, [form.id]);

  // Handle drag and drop for sections
  const onDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(form.sections);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setForm(prev => ({
      ...prev,
      sections: items
    }));
  };

  // Save form to the server
  const saveForm = useCallback(async (publish = false) => {
    try {
      setIsSaving(true);

      // Prepare form data
      const formData = {
        title: form.title,
        description: form.description,
        event: eventId,
        is_published: publish,
      };

      let savedForm;

      // Create or update the form
      if (form.id) {
        savedForm = await formBuilderService.eventForm.update(form.id, formData);
      } else {
        savedForm = await formBuilderService.eventForm.create(formData);
      }

      // Save sections and questions
      const savedSections = [];
      for (const section of form.sections) {
        let savedSection;
        const sectionData = {
          title: section.title,
          description: section.description,
          order: section.order || 0,
        };

        if (section.id && !section.isNew) {
          savedSection = await formBuilderService.section.update(section.id, sectionData);
        } else {
          savedSection = await formBuilderService.section.create(savedForm.id, sectionData);
        }

        // Save questions
        const savedQuestions = [];
        for (const question of section.questions || []) {
          const questionData = {
            field_type: question.type,
            label: question.label,
            required: question.required,
            placeholder: question.placeholder,
            description: question.description,
            options: question.options || [],
            order: question.order || 0,
          };

          let savedQuestion;
          if (question.id && !question.isNew) {
            savedQuestion = await formBuilderService.question.update(question.id, questionData);
          } else {
            savedQuestion = await formBuilderService.question.create(savedSection.id, questionData);
          }

          savedQuestions.push({
            ...savedQuestion,
            options: savedQuestion.options || [],
          });
        }

        savedSections.push({
          ...savedSection,
          questions: savedQuestions,
        });
      }

      // Update the form with saved data
      const updatedForm = {
        ...savedForm,
        sections: savedSections,
      };

      setForm(updatedForm);

      // Show success message
      setSaveStatus({
        open: true,
        message: publish ? 'Form published successfully!' : 'Form saved as draft.',
        severity: 'success',
      });

      // Notify parent components
      if (publish && onPublish) {
        onPublish(updatedForm);
      } else if (onSave) {
        onSave(updatedForm);
      }

      return updatedForm;
    } catch (error) {
      console.error('Error saving form:', error);
      const errorMessage = error.response?.data?.message || 'Failed to save form';
      setSaveStatus({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [form, eventId, onPublish, onSave]);

  // Handle form submission
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      await saveForm();
    } catch (error) {
      // Error handling is done in saveForm
    } finally {
      setIsSubmitting(false);
    }
  }, [saveForm]);

  // Handle publish button click
  const handlePublish = useCallback(async () => {
    try {
      setIsSubmitting(true);
      await saveForm(true);
    } catch (error) {
      // Error handling is done in saveForm
    } finally {
      setIsSubmitting(false);
    }
  }, [saveForm]);

  // Close the status snackbar
  const handleCloseSnackbar = useCallback(() => {
    setSaveStatus(prev => ({ ...prev, open: false }));
  }, []);

  if (isLoading) {
    return (
      <Backdrop open={isLoading} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ position: 'relative' }}>
      <Box mb={3}>
        <TextField
          fullWidth
          variant="outlined"
          label="Form Title"
          value={form.title}
          onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
          margin="normal"
          required
        />
        <TextField
          fullWidth
          variant="outlined"
          label="Description"
          value={form.description}
          onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
          margin="normal"
          multiline
          rows={2}
        />
      </Box>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="sections" type="SECTION">
          {(provided) => (
            <Box {...provided.droppableProps} ref={provided.innerRef}>
              {form.sections.map((section, index) => (
                <Draggable
                  key={section.id}
                  draggableId={`section-${section.id}`}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      mb={3}
                    >
                      <SectionEditor
                        key={section.id}
                        section={section}
                        onUpdate={updateSection}
                        onDelete={deleteSection}
                        index={index}
                        isDragging={snapshot.isDragging}
                        dragHandleProps={provided.dragHandleProps}
                        onFieldUpdate={(updatedSection) => {
                          // Handle field updates within the section
                          updateSection(section.id, updatedSection);
                        }}
                        onAddField={(updatedSection) => {
                          // Handle field additions within the section
                          updateSection(section.id, updatedSection);
                        }}
                      />
                    </Box>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>

      <Box mt={2} mb={4} display="flex" justifyContent="center">
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addSection}
          sx={{ mr: 2 }}
        >
          Add Section
        </Button>
      </Box>

      <Box mt={4} pt={2} borderTop={1} borderColor="divider">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
              sx={{ mr: 2 }}
            >
              {isSaving ? 'Saving...' : 'Save Draft'}
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handlePublish}
              disabled={isSubmitting}
              startIcon={isSaving ? <CircularProgress size={20} /> : <PublishIcon />}
            >
              {isSaving ? 'Publishing...' : 'Publish Form'}
            </Button>
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={saveStatus.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={saveStatus.severity}>
          {saveStatus.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FormBuilder;
